import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Switch,
  FormControlLabel,
  CircularProgress,
  IconButton,
  Snackbar,
  Alert,
  Chip,
  Card,
  CardContent,
  CardActions,
  Tooltip,
  InputAdornment,
  Link,
  Stack
} from '@mui/material';
import DialogWrapper from '@/components/DialogWrapper';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  Security as SecurityIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { Model, ModelCreate, ModelUpdate } from '@/api/models';
import { getUserById } from '@/api/userSettings';
import {
  useAdminModelsQuery,
  useCreateModelMutation,
  useDeleteModelMutation,
  useToggleModelActiveMutation,
  useToggleModelImplicitMutation,
  useUpdateModelMutation,
} from '@/features/admin/hooks/useAdminResourcesQuery';

const ModelsTab: React.FC = () => {
  const [models, setModels] = useState<Model[]>([]);
  const [filteredModels, setFilteredModels] = useState<Model[]>([]);
  const [loading, setLoading] = useState(true);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info'>('success');
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingModel, setEditingModel] = useState<Model | null>(null);
  const [deletingModel, setDeletingModel] = useState<Model | null>(null);
  const [createdByName, setCreatedByName] = useState<string | null>(null);
  const [userCache, setUserCache] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<ModelCreate>({
    name: '',
    endpoint: '',
    deployment: '',
    api_version: '',
    api_path: '',
    is_active: true,
    is_implicit: false,
    supports_images: false,
    description: '',
    max_tokens: 4000,
    pricing_input_1m_tokens: 0,
    pricing_output_1m_tokens: 0
  });
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingActiveId, setTogglingActiveId] = useState<string | null>(null);
  const [togglingImplicitId, setTogglingImplicitId] = useState<string | null>(null);
  const [targetUri, setTargetUri] = useState('');
  const [showAdvancedFields, setShowAdvancedFields] = useState(true);

  const {
    data: queriedModels,
    isPending: isModelsPending,
    refetch: refetchModels,
  } = useAdminModelsQuery();
  const createModelMutation = useCreateModelMutation();
  const updateModelMutation = useUpdateModelMutation();
  const deleteModelMutation = useDeleteModelMutation();
  const toggleModelActiveMutation = useToggleModelActiveMutation();
  const toggleModelImplicitMutation = useToggleModelImplicitMutation();

  useEffect(() => {
    if (!queriedModels) {
      return;
    }

    setModels(queriedModels);
    setFilteredModels(queriedModels);
  }, [queriedModels]);

  useEffect(() => {
    setLoading(isModelsPending);
  }, [isModelsPending]);

  const fetchModels = async () => {
    try {
      await refetchModels();
    } catch (error) {
      showSnackbar('Failed to fetch models', 'error');
      console.error('Error fetching models:', error);
    }
  };

  const showSnackbar = (message: string, severity: 'success' | 'error' | 'info') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  };

  const handleCreateModel = async () => {
    setCreating(true);
    try {
      await createModelMutation.mutateAsync(formData);
      showSnackbar('Model created successfully', 'success');
      // close dialog only after successful creation
      setCreateDialogOpen(false);
      resetFormData();
    } catch (error) {
      showSnackbar('Failed to create model', 'error');
      console.error('Error creating model:', error);
    } finally {
      setCreating(false);
    }
  };

  const validateCreateForm = () => {
    const required = ['name', 'deployment', 'endpoint', 'api_version', 'api_path'];
    for (const field of required) {
      // @ts-ignore
      if (!formData[field] || (typeof formData[field] === 'string' && formData[field].trim() === '')) {
        showSnackbar(`Please fill the required field: ${field}`, 'error');
        return false;
      }
    }
    return true;
  };

  const handleUpdateModel = async () => {
    if (!editingModel) return;
    setUpdating(true);
    try {
      const updateData: ModelUpdate = {
        name: formData.name,
        // allow updating endpoint as well
        endpoint: formData.endpoint,
        deployment: formData.deployment,
        api_version: formData.api_version,
        api_path: formData.api_path,
        is_active: formData.is_active,
        is_implicit: formData.is_implicit,
        supports_images: formData.supports_images,
        description: formData.description,
        max_tokens: formData.max_tokens,
        pricing_input_1m_tokens: formData.pricing_input_1m_tokens,
        pricing_output_1m_tokens: formData.pricing_output_1m_tokens
      };

      await updateModelMutation.mutateAsync({ modelId: editingModel.id, payload: updateData });
      showSnackbar('Model updated successfully', 'success');
      setEditDialogOpen(false);
      setEditingModel(null);
      resetFormData();
    } catch (error) {
      showSnackbar('Failed to update model', 'error');
      console.error('Error updating model:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteModel = async () => {
    if (!deletingModel) return;

    setDeleting(true);
    try {
      await deleteModelMutation.mutateAsync(deletingModel.id);
      showSnackbar('Model deleted successfully', 'success');
      setDeleteDialogOpen(false);
      setDeletingModel(null);
    } catch (error) {
      showSnackbar('Failed to delete model', 'error');
      console.error('Error deleting model:', error);
    } finally {
      setDeleting(false);
    }
  };

  const handleToggleActive = async (model: Model) => {
    setTogglingActiveId(model.id || null);
    const original = model.is_active;
    // optimistic flip
    setModels(prev => prev.map(m => (m.id === model.id ? { ...(m as Model), is_active: !original } as Model : m)));
    setFilteredModels(prev => prev.map(m => (m.id === model.id ? { ...(m as Model), is_active: !original } as Model : m)));
    try {
      await toggleModelActiveMutation.mutateAsync(model.id);
      showSnackbar(`Model ${original ? 'deactivated' : 'activated'} successfully`, 'success');
    } catch (error) {
      // revert
      setModels(prev => prev.map(m => (m.id === model.id ? { ...(m as Model), is_active: original } as Model : m)));
      setFilteredModels(prev => prev.map(m => (m.id === model.id ? { ...(m as Model), is_active: original } as Model : m)));
      showSnackbar('Failed to update model status', 'error');
      console.error('Error toggling model status:', error);
    } finally {
      setTogglingActiveId(null);
    }
  };

  const handleToggleImplicit = async (model: Model) => {
    setTogglingImplicitId(model.id || null);
    const original = model.is_implicit;
    setModels(prev => prev.map(m => (m.id === model.id ? { ...(m as Model), is_implicit: !original } as Model : m)));
    setFilteredModels(prev => prev.map(m => (m.id === model.id ? { ...(m as Model), is_implicit: !original } as Model : m)));
    try {
      await toggleModelImplicitMutation.mutateAsync(model.id);
      showSnackbar(`Model access changed to ${original ? 'explicit' : 'implicit'}`, 'success');
    } catch (error) {
      setModels(prev => prev.map(m => (m.id === model.id ? { ...(m as Model), is_implicit: original } as Model : m)));
      setFilteredModels(prev => prev.map(m => (m.id === model.id ? { ...(m as Model), is_implicit: original } as Model : m)));
      showSnackbar('Failed to update model access', 'error');
      console.error('Error toggling model access:', error);
    } finally {
      setTogglingImplicitId(null);
    }
  };

  const resetFormData = () => {
    setFormData({
      name: '',
      endpoint: '',
      deployment: '',
      api_version: '',
      api_path: '',
      is_active: true,
      is_implicit: false,
      supports_images: false,
      description: '',
      max_tokens: 4000,
      pricing_input_1m_tokens: 0,
      pricing_output_1m_tokens: 0
    });
    setCreatedByName(null);
    setTargetUri('');
    setShowAdvancedFields(true);
  };

  const parseFoundryTargetUri = (uri: string) => {
    setTargetUri(uri);
    if (!uri.trim()) {
      setShowAdvancedFields(true);
      return;
    }
    try {
      const url = new URL(uri.trim());
      const origin = url.origin;
      const pathname = url.pathname;
      const apiVersion = url.searchParams.get('api-version') || '';
      const deploymentMatch = pathname.match(/\/deployments\/([^/]+)/);
      const deployment = deploymentMatch ? deploymentMatch[1] : '';
      setFormData(prev => ({
        ...prev,
        endpoint: origin,
        api_path: pathname,
        ...(apiVersion && { api_version: apiVersion }),
        ...(deployment && { deployment }),
      }));
      setShowAdvancedFields(false);
    } catch {
      // Not a valid URL yet — leave individual fields unchanged
    }
  };

  const openCreateDialog = () => {
    resetFormData();
    setCreateDialogOpen(true);
  };

  const openEditDialog = (model: Model) => {
    setEditingModel(model);
    setFormData({
      name: model.name,
      endpoint: model.endpoint,
      deployment: model.deployment,
      api_version: model.api_version,
      api_path: model.api_path,
      is_active: model.is_active,
      is_implicit: model.is_implicit,
      supports_images: model.supports_images,
      description: model.description || '',
      max_tokens: model.max_tokens || 4000,
      pricing_input_1m_tokens: model.pricing_input_1m_tokens ?? 0,
      pricing_output_1m_tokens: model.pricing_output_1m_tokens ?? 0
    });
    setCreatedByName(null);
    // Resolve created_by to display name using cache to avoid repeated requests
    (async () => {
      try {
        if (model.created_by) {
          if (userCache[model.created_by]) {
            setCreatedByName(userCache[model.created_by]);
          } else {
            const user = await getUserById(model.created_by as string);
            const name = user?.name || user?.email || model.created_by;
            setUserCache(prev => ({ ...prev, [model.created_by as string]: name }));
            setCreatedByName(name);
          }
        } else {
          setCreatedByName(null);
        }
      } catch (err) {
        console.error('Failed to fetch user for created_by mapping', err);
        setCreatedByName(model.created_by || null);
      }
    })();

    setTargetUri('');
    setShowAdvancedFields(true);
    setEditDialogOpen(true);
  };

  const openDeleteDialog = (model: Model) => {
    setDeletingModel(model);
    setDeleteDialogOpen(true);
  };

  const getModelStatusColor = (model: Model) => {
    if (!model.is_active) return '#f44336';
    if (model.is_implicit) return '#4CAF50';
    return '#FF9800';
  };

  const getModelStatusText = (model: Model) => {
    if (!model.is_active) return 'Inactive';
    if (model.is_implicit) return 'Implicit';
    return 'Explicit';
  };

  // Derived: whether the 4 technical fields (Deployment, API Version, API Path, Endpoint URL) are expanded
  const techVisible = !targetUri || showAdvancedFields;

  return (
    <Box sx={{ width: '100%', height: '100%', pl: 2, pr: 2, pt: 0, pb: 2 }}>
      <Box display="flex" justifyContent="flex-end" alignItems="center" mb={1}>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Refresh">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={fetchModels}
                disabled={loading}
                aria-label="refresh-models"
              >
                <RefreshIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="New model">
            <span>
              <IconButton
                size="small"
                color="primary"
                onClick={openCreateDialog}
                aria-label="add-model"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Box>

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : filteredModels.length === 0 ? (
        <Box sx={{ p: 3 }}>
          <Typography color="text.secondary">No models found.</Typography>
        </Box>
      ) : (
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: {
              xs: '1fr',
              sm: 'repeat(auto-fit, minmax(280px, 1fr))',
              lg: 'repeat(auto-fit, minmax(320px, 1fr))'
            },
            gap: 2
          }}
        >
          {filteredModels.map((model) => (
            <Box key={model.id || model.name}>
              <Card sx={{ height: '100%', position: 'relative' }}>
                <CardContent>
                  {/* Top row: model name */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="h6" component="h2" sx={{ fontWeight: 'bold' }}>
                      {model.name}
                    </Typography>
                  </Box>

                  {/* Endpoint (masked) */}
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    <strong>Endpoint:</strong> {model.endpoint ? `${model.endpoint.substring(0, 30)}...` : 'Not set'}
                  </Typography>

                  {/* Timestamps */}
                  <Typography variant="caption" color="text.secondary">
                    Created: {model.created_date ? new Date(model.created_date).toLocaleDateString() : '—'}
                  </Typography>
                  {model.updated_date && (
                    <Typography variant="caption" color="text.secondary" display="block">
                      Updated: {new Date(model.updated_date).toLocaleDateString()}
                    </Typography>
                  )}
                </CardContent>

                <CardActions sx={{ justifyContent: 'space-between', px: 2, pb: 2 }}>
                  <Stack direction="row" spacing={0.5} alignItems="center">
                    <Tooltip title={model.is_active ? 'Deactivate' : 'Activate'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(model)}
                          sx={{ color: model.is_active ? 'green' : 'rgba(0,0,0,0.6)' }}
                          aria-label={model.is_active ? 'deactivate' : 'activate'}
                          disabled={!!togglingActiveId}
                        >
                          {togglingActiveId === model.id ? (
                            <CircularProgress size={18} />
                          ) : model.is_active ? (
                            <VisibilityIcon fontSize="small" />
                          ) : (
                            <VisibilityOffIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title={model.is_implicit ? 'Make Explicit' : 'Make Implicit'}>
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleImplicit(model)}
                          sx={{ color: model.is_implicit ? '#1976d2' : 'rgba(0,0,0,0.6)' }}
                          aria-label={model.is_implicit ? 'make-explicit' : 'make-implicit'}
                          disabled={!!togglingImplicitId}
                        >
                          {togglingImplicitId === model.id ? (
                            <CircularProgress size={18} />
                          ) : (
                            <SecurityIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Edit Model">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openEditDialog(model)}
                          sx={{ color: '#1976d2' }}
                          aria-label="edit-model"
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => openDeleteDialog(model)}
                          sx={{ color: '#d32f2f' }}
                          aria-label="delete-model"
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                  <Box>
                    <Chip label={getModelStatusText(model)} sx={{ backgroundColor: getModelStatusColor(model), color: 'white', fontSize: '0.75rem' }} size="small" />
                  </Box>
                </CardActions>
              </Card>
            </Box>
          ))}
        </Box>
      )}
      <Snackbar open={snackbarOpen} autoHideDuration={4000} onClose={() => setSnackbarOpen(false)}>
        <Alert onClose={() => setSnackbarOpen(false)} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
      {/* Create Dialog */}
      <DialogWrapper open={createDialogOpen} onClose={() => { if (!creating && !loading) { setCreateDialogOpen(false); resetFormData(); } }} maxWidth="md" fullWidth>
        <DialogTitle>Add Model</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' }, gap: 4, mt: 1 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                fullWidth
                label="Target URI (Microsoft Foundry)"
                value={targetUri}
                onChange={(e) => parseFoundryTargetUri(e.target.value)}
                placeholder="https://{resource}.openai.azure.com/openai/deployments/{deployment}/chat/completions?api-version=..."
                helperText="Paste the Target URI from Microsoft Foundry to auto-fill the fields below"
                disabled={creating}
              />
            </Box>
            <Box sx={{ gridColumn: techVisible ? 'auto' : '1 / -1' }}>
              <TextField autoFocus fullWidth label="Model Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} disabled={creating} />
            </Box>
            <Box sx={{ display: techVisible ? undefined : 'none', opacity: targetUri ? 0.72 : 1, transition: 'opacity 0.2s' }}>
              <TextField fullWidth label="Deployment" value={formData.deployment} onChange={(e) => setFormData({ ...formData, deployment: e.target.value })} disabled={creating} />
            </Box>
            {targetUri && (
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', mt: -2 }}>
                <Typography variant="caption" sx={{ color: 'text.disabled', flexGrow: 1 }}>
                  Technical fields auto-filled from Target URI — edit only if needed
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => setShowAdvancedFields(v => !v)}
                  sx={{ fontSize: '0.75rem', cursor: 'pointer', ml: 1 }}
                >
                  {showAdvancedFields ? 'Hide' : 'Show'}
                </Link>
              </Box>
            )}
            <Box sx={{ display: techVisible ? undefined : 'none', opacity: targetUri ? 0.72 : 1, transition: 'opacity 0.2s' }}>
              <TextField fullWidth label="API Version" value={formData.api_version} onChange={(e) => setFormData({ ...formData, api_version: e.target.value })} disabled={creating} />
            </Box>
            <Box sx={{ display: techVisible ? undefined : 'none', opacity: targetUri ? 0.72 : 1, transition: 'opacity 0.2s' }}>
              <TextField fullWidth label="API Path" value={formData.api_path} onChange={(e) => setFormData({ ...formData, api_path: e.target.value })} disabled={creating} />
            </Box>
            <Box sx={{ display: techVisible ? undefined : 'none', opacity: targetUri ? 0.72 : 1, transition: 'opacity 0.2s' }}>
              <TextField fullWidth label="Endpoint URL" value={formData.endpoint} onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })} helperText="Base URL only (no path)" disabled={creating} />
            </Box>
            <Box>
              <TextField fullWidth label="Max Tokens" type="number" value={formData.max_tokens} onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) || 4000 })} inputProps={{ min: 1, max: 100000 }} helperText="Maximum number of tokens" disabled={creating} />
            </Box>

            <Box>
              <TextField fullWidth label="Input Price (per 1M tokens)" type="number" value={formData.pricing_input_1m_tokens} onChange={(e) => setFormData({ ...formData, pricing_input_1m_tokens: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, step: 0.01 }} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} helperText="Price in EUR per 1 million input tokens" disabled={creating} />
            </Box>
            <Box>
              <TextField fullWidth label="Output Price (per 1M tokens)" type="number" value={formData.pricing_output_1m_tokens} onChange={(e) => setFormData({ ...formData, pricing_output_1m_tokens: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, step: 0.01 }} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} helperText="Price in EUR per 1 million output tokens" disabled={creating} />
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Description" multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} disabled={creating} />
            </Box>

            <Box>
              <FormControlLabel control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />} label="Active" />
            </Box>
            <Box>
              <FormControlLabel control={<Switch checked={formData.is_implicit} onChange={(e) => setFormData({ ...formData, is_implicit: e.target.checked })} />} label="Implicit Access" />
            </Box>
            <Box>
              <FormControlLabel control={<Switch checked={!!formData.supports_images} onChange={(e) => setFormData({ ...formData, supports_images: e.target.checked })} />} label="Supports image input" />
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Link
            href="https://azure.microsoft.com/en-us/pricing/details/cognitive-services/openai-service/"
            target="_blank"
            rel="noopener"
            variant="body2"
            sx={{ fontSize: '0.8rem', ml: 2 }}
          >
            See Azure OpenAI pricing
          </Link>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => { setCreateDialogOpen(false); resetFormData(); }} color="primary" variant="contained" sx={{ mr: 1 }} disabled={creating}>Cancel</Button>
            <Button onClick={() => { if (validateCreateForm()) { handleCreateModel(); } }} color="primary" variant="contained" disabled={creating} startIcon={creating ? <CircularProgress size={16} /> : undefined}>{creating ? 'Creating...' : 'Create'}</Button>
          </Box>
        </DialogActions>
      </DialogWrapper>

      {/* Edit Model Dialog */}
      <DialogWrapper open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Model</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)' }, gap: 4, mt: 1 }}>
            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField
                fullWidth
                label="Target URI (Microsoft Foundry)"
                value={targetUri}
                onChange={(e) => parseFoundryTargetUri(e.target.value)}
                placeholder="https://{resource}.openai.azure.com/openai/deployments/{deployment}/chat/completions?api-version=..."
                helperText="Paste a new Target URI from Microsoft Foundry to update the fields below"
              />
            </Box>
            <Box sx={{ gridColumn: techVisible ? 'auto' : '1 / -1' }}>
              <TextField fullWidth label="Model Name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} helperText="Users will see this name" />
            </Box>
            <Box sx={{ display: techVisible ? undefined : 'none', opacity: targetUri ? 0.72 : 1, transition: 'opacity 0.2s' }}>
              <TextField fullWidth label="Deployment" value={formData.deployment} onChange={(e) => setFormData({ ...formData, deployment: e.target.value })} />
            </Box>
            {targetUri && (
              <Box sx={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', mt: -2 }}>
                <Typography variant="caption" sx={{ color: 'text.disabled', flexGrow: 1 }}>
                  Technical fields auto-filled from Target URI — edit only if needed
                </Typography>
                <Link
                  component="button"
                  type="button"
                  variant="caption"
                  onClick={() => setShowAdvancedFields(v => !v)}
                  sx={{ fontSize: '0.75rem', cursor: 'pointer', ml: 1 }}
                >
                  {showAdvancedFields ? 'Hide' : 'Show'}
                </Link>
              </Box>
            )}
            <Box sx={{ display: techVisible ? undefined : 'none', opacity: targetUri ? 0.72 : 1, transition: 'opacity 0.2s' }}>
              <TextField fullWidth label="API Version" value={formData.api_version} onChange={(e) => setFormData({ ...formData, api_version: e.target.value })} />
            </Box>
            <Box sx={{ display: techVisible ? undefined : 'none', opacity: targetUri ? 0.72 : 1, transition: 'opacity 0.2s' }}>
              <TextField fullWidth label="API Path" value={formData.api_path} onChange={(e) => setFormData({ ...formData, api_path: e.target.value })} />
            </Box>
            <Box sx={{ display: techVisible ? undefined : 'none', opacity: targetUri ? 0.72 : 1, transition: 'opacity 0.2s' }}>
              <TextField fullWidth label="Endpoint URL" value={formData.endpoint} onChange={(e) => setFormData({ ...formData, endpoint: e.target.value })} helperText="Base URL only (no path)" />
            </Box>
            <Box>
              <TextField fullWidth label="Max Tokens" type="number" value={formData.max_tokens} onChange={(e) => setFormData({ ...formData, max_tokens: parseInt(e.target.value) || 4000 })} inputProps={{ min: 1, max: 100000 }} helperText="Maximum number of tokens" />
            </Box>

            <Box>
              <TextField fullWidth label="Input Price (per 1M tokens)" type="number" value={formData.pricing_input_1m_tokens} onChange={(e) => setFormData({ ...formData, pricing_input_1m_tokens: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, step: 0.01 }} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} helperText="Price in EUR per 1 million input tokens" />
            </Box>
            <Box>
              <TextField fullWidth label="Output Price (per 1M tokens)" type="number" value={formData.pricing_output_1m_tokens} onChange={(e) => setFormData({ ...formData, pricing_output_1m_tokens: parseFloat(e.target.value) || 0 })} inputProps={{ min: 0, step: 0.01 }} InputProps={{ startAdornment: <InputAdornment position="start">€</InputAdornment> }} helperText="Price in EUR per 1 million output tokens" />
            </Box>

            <Box sx={{ gridColumn: '1 / -1' }}>
              <TextField fullWidth label="Description" multiline rows={3} value={formData.description} onChange={(e) => setFormData({ ...formData, description: e.target.value })} />
            </Box>

            <Box>
              <FormControlLabel control={<Switch checked={formData.is_active} onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })} />} label="Active" />
            </Box>
            <Box>
              <FormControlLabel control={<Switch checked={formData.is_implicit} onChange={(e) => setFormData({ ...formData, is_implicit: e.target.checked })} />} label="Implicit Access" />
            </Box>
            <Box>
              <FormControlLabel control={<Switch checked={!!formData.supports_images} onChange={(e) => setFormData({ ...formData, supports_images: e.target.checked })} />} label="Supports image input" />
            </Box>

            <Box>
              <TextField fullWidth label="Created By" value={createdByName || editingModel?.created_by || ''} disabled />
            </Box>
            <Box>
              <TextField fullWidth label="Created" value={editingModel?.created_date ? new Date(editingModel.created_date).toLocaleString() : ''} disabled />
            </Box>
            {editingModel?.updated_date && (
              <Box sx={{ gridColumn: '1 / -1' }}>
                <TextField fullWidth label="Last Updated" value={new Date(editingModel.updated_date).toLocaleString()} disabled />
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between' }}>
          <Link 
            href="https://azure.microsoft.com/en-us/pricing/details/cognitive-services/openai-service/" 
            target="_blank" 
            rel="noopener"
            variant="body2"
            sx={{ fontSize: '0.8rem', ml: 2 }}
          >
            See Azure OpenAI pricing
          </Link>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => setEditDialogOpen(false)} color="primary" variant="contained" sx={{ mr: 1 }} disabled={updating}>
              Cancel
            </Button>
            <Button onClick={handleUpdateModel} variant="contained" color="primary" disabled={updating} startIcon={updating ? <CircularProgress size={16} /> : undefined}>
              {updating ? 'Updating...' : 'Update Model'}
            </Button>
          </Box>
        </DialogActions>
      </DialogWrapper>

      {/* Delete Dialog */}
      <DialogWrapper open={deleteDialogOpen} onClose={() => { if (!deleting) { setDeleteDialogOpen(false); setDeletingModel(null); } }} maxWidth="sm" fullWidth>
        <DialogTitle>Delete Model</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete model "{deletingModel?.name}"? This action cannot be undone.</Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-end' }}>
          <Box sx={{ pr: 2 }}>
            <Button onClick={() => { setDeleteDialogOpen(false); setDeletingModel(null); }} color="primary" variant="contained" sx={{ mr: 1 }} disabled={deleting}>Cancel</Button>
            <Button onClick={handleDeleteModel} color="error" variant="contained" disabled={deleting} startIcon={deleting ? <CircularProgress size={16} /> : undefined}>
              {deleting ? 'Deleting...' : 'Delete'}
            </Button>
          </Box>
        </DialogActions>
      </DialogWrapper>
    </Box>
  );
};

export default ModelsTab;
